/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { CARD_TYPES } from 'carbon-addons-iot-react';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import hash from 'object-hash';
import PubSub from 'pubsub-js';

import { usePrevious } from 'customHooks';
import { makeCancelable } from 'Utils';

const propTypes = {
  card: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  setCard: PropTypes.func,
  /** Callback function to use when a data fetch is required, should return an object with the card data */
  /** we can't fetch data here directly, we have to call all the way to DashboardRenderer because they're the only ones that know the dimensionFilters */
  onFetchData: PropTypes.func,
  /** the moment start date passed from CardRenderer */
  startDate: PropTypes.any, // eslint-disable-line react/forbid-prop-types
  /** the moment end date passed from CardRenderer */
  endDate: PropTypes.any, // eslint-disable-line react/forbid-prop-types
  /** the correct grain to fetch on */
  grain: PropTypes.string,
  dataItems: PropTypes.arrayOf(PropTypes.object),
  filter: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

// Work around for Alert By Status VALUE cards using the new v2 alert apis that return an object: { New: 125, Validated: 5 }
// rather than an array: [{ status: 'New', count: 125 }, { status: 'Validated' }]
export const formatCardData = (card, data) => {
  if (data && !Array.isArray(data) && card?.type === CARD_TYPES.VALUE) {
    return Object.entries(data).map(([status, count]) => ({
      [card.dataSource?.groupBy?.[0]]: status,
      [card.dataSource?.attributes?.[0]?.id]: count,
    }));
  }
  return data;
};

/** Asynchronous reusable function to load Card Data */
export const loadCardData = (
  card,
  startDate,
  endDate,
  grain,
  setIsLoading,
  setData,
  setAdditionalData,
  setDataError,
  onFetchData,
  filter
) => {
  // Set state to loading
  setIsLoading(true);

  // onFetchData callback goes all the way up to the handleFetchData on DashboardRenderer
  const cancelableFetchPromise = makeCancelable(
    onFetchData(card, startDate, endDate, grain, filter)
  );
  // wait for the cancelable promise to resolve but return it so it can be canceled
  cancelableFetchPromise
    .then(({ data, additionalData, dataError }) => {
      if (!dataError) {
        setData(formatCardData(card, data));
        setAdditionalData(additionalData);
        setIsLoading(false);
      } else {
        setDataError(dataError);
        setIsLoading(false);
      }
    })
    .catch(error => {
      console.error('error fetching card data', error);
    });

  return cancelableFetchPromise;
};

/**
 * Fire the load card data functions based on inbound props, he determines when the card needs to fetch updated data.
 * He keeps track of the card's currently loaded data and whether or not the card is actively loading
 * He also passes the fetched data (and optionally additionalData) to his children as a data prop, passes the isLoading prop to the Card Mapper if the data is loading
 * It watches the isDashboardLoading bit to see if the card needs to trigger a data reload.  If the
 * isDashboardLoading bit changes to true, it will trigger the dashboard's
 * onFetchData function to retrieve the updated card properties for each card.
 */
const CardFetcher = ({
  card,
  onFetchData,
  onDataFetched,
  children,
  startDate,
  endDate,
  grain,
  dataItems,
  filter,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(card.data);
  const [additionalData, setAdditionalData] = useState([]);
  const [dataError, setDataError] = useState();

  /** need to keep track so we know whether we need to fetch */
  const previousStartDate = usePrevious(startDate);
  const previousEndDate = usePrevious(endDate);
  const previousGrain = usePrevious(grain);
  const previousCardData = usePrevious(card.data);
  const previousOnFetchData = usePrevious(onFetchData);

  const [needsRefresh, setNeedsRefresh] = useState(false);

  // This is the subscriber token used to listen for refresh evenets
  const refreshToken = useRef();

  // Subscribe to the refresh event
  useEffect(() => {
    if (refreshToken) {
      refreshToken.current = PubSub.subscribe('refresh', () => {
        // this will force the setupAndLoadCard to fire below
        setNeedsRefresh(true);
      });
    }
    return () => PubSub.unsubscribe(refreshToken.current);
  }, []);

  // if the data is changed from the outside, we need to update it locally (this can happen when modifying a card directly like in the case of the alert card) otherwise the old state will be used
  useEffect(() => {
    if (!isEqual(card.data, previousCardData)) {
      setData(card.data);
    }
  }, [card.data, previousCardData]);

  // Needed for the load card useEffect deps
  const cardDataSourceHash = useMemo(() => hash(card?.dataSource || {}), [card?.dataSource]);
  const { endDateMS, startDateMS } = useMemo(
    () => ({ endDateMS: endDate?.valueOf(), startDateMS: startDate?.valueOf() }),
    [endDate, startDate]
  );

  // If the start dates and end dates have changed, refetch the data and setup image cards
  useEffect(() => {
    let cancelableLoadPromise;
    const setupAndLoadCard = async () => {
      // Use the updated dataSource information while fetching data, stop unnecessary fetches even if useEffect fires again
      if (
        !isNil(onFetchData) &&
        card.dataSource &&
        startDate &&
        endDate &&
        (!previousStartDate?.isSame(startDate) || // because initial previousStartDate is null this will force a fetch on initial render
          !previousEndDate?.isSame(endDate) ||
          previousGrain !== grain ||
          needsRefresh ||
          onFetchData !== previousOnFetchData)
      ) {
        // only fetch if there's a onFetchData defined and a dataSource
        cancelableLoadPromise = loadCardData(
          card,
          startDate,
          endDate,
          grain,
          setIsLoading,
          setData,
          setAdditionalData,
          setDataError,
          onFetchData,
          filter
        );
        // wait for the load promise to finish before updating
        await cancelableLoadPromise;
      } else {
        setIsLoading(false);
      }
      // After fetching or if we skip notify the parent dashboard
      onDataFetched(card.id);
      setNeedsRefresh(false);
    };

    setupAndLoadCard();

    return () => {
      // if the component unmounts, cancel the setup and Load promise
      // eslint-disable-next-line no-unused-expressions
      cancelableLoadPromise?.cancel();
      console.warn(
        `canceling promise for card, should only happen if we leave the dashboard or reload while fetching`,
        card?.id
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cardDataSourceHash, // only the card data source needs to be examined
    endDateMS, // have to use MS because the moment object might be regenerated
    onDataFetched,
    onFetchData, // TODO fix dependencies and have clear info on whether or not to make the API call on this render
    needsRefresh,
    startDateMS, // have to use MS because the moment object might be regenerated
    grain,
  ]);

  // Throw a runtime error if children isn't a single element
  React.Children.only(children);
  return React.cloneElement(children, {
    cardProps: {
      ...card,
      isLoading,
      isLazyLoading: card?.type === CARD_TYPES.TIMESERIES && data?.length > 500, // lazy load if we're large
      error: dataError,
    },
    grain,
    data,
    additionalData,
    startDate,
    endDate,
    dataItems,
  });
};

CardFetcher.propTypes = propTypes;
export default CardFetcher;
